import { useToast } from "@design";
import { type QueryKey, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { errorMessage } from "@/api";

/** Resolved (already-translated) toast messages; `created`/`createFailed` only for forms that create. */
export interface EntitySaveMessages {
  saved: string;
  saveFailed: string;
  created?: string;
  createFailed?: string;
}

/**
 * The save/create flow every Build entity form shares: run the request,
 * refresh the affected queries, toast, then reset (save) or open the new
 * entity (create). Failures toast the backend's message. Each form keeps only
 * what's specific to it: the request body and which queries to refresh.
 */
export function useEntitySave(messages: EntitySaveMessages) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const refresh = (keys: readonly QueryKey[]) =>
    Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));

  const saveExisting = async ({
    request,
    invalidate,
    onSaved,
  }: {
    request: () => Promise<unknown>;
    invalidate: readonly QueryKey[];
    onSaved?: () => void;
  }) => {
    try {
      await request();
      await refresh(invalidate);
      toast.success(messages.saved);
      onSaved?.();
    } catch (error) {
      toast.error(errorMessage(error, messages.saveFailed));
    }
  };

  const createNew = async ({
    request,
    invalidate,
    to,
  }: {
    /** Creates the entity and resolves its uuid. */
    request: () => Promise<string>;
    invalidate: readonly QueryKey[];
    /** Where to go once created. */
    to: (uuid: string) => string;
  }) => {
    try {
      const uuid = await request();
      await refresh(invalidate);
      toast.success(messages.created ?? messages.saved);
      navigate(to(uuid));
    } catch (error) {
      toast.error(errorMessage(error, messages.createFailed ?? messages.saveFailed));
    }
  };

  return { saveExisting, createNew };
}
